'use client';

/**
 * PriceBreakdown Component
 *
 * Displays a fee breakdown with line items grouped by category.
 * Supports subtotals, discounts, taxes, and a grand total.
 * Uses glassmorphism styling with optional neon accent colors.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { useMemo } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import type { PriceBreakdownProps, PriceLineItem } from '../../types/pricingEngine';
import { transientOptions } from '../../utils/styledUtils';
import { LineItem } from './LineItem';

// ============================================================================
// Types
// ============================================================================

interface CategoryGroup {
  category: string;
  items: PriceLineItem[];
  subtotal: number;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  if (neonStyles) {
    return {
      borderRadius: 12,
      padding: theme.spacing(2),
      background: neonStyles.background,
      border: `1px solid ${neonStyles.border}`,
      transition: transitionPresets.card,
    };
  }

  return {
    borderRadius: 12,
    padding: theme.spacing(2),
    background: glass.background,
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: glass.shadow,
    transition: transitionPresets.card,
  };
});

const CategoryHeader = styled(
  Typography,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: neonStyles?.base || theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),

    '&:first-of-type': {
      marginTop: 0,
    },
  };
});

const SummarySection = styled(
  Box,
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => ({
  marginTop: 16,
  paddingTop: 16,
  borderTop: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
}));

// ============================================================================
// Component
// ============================================================================

export const PriceBreakdown: React.FC<PriceBreakdownProps & Omit<BoxProps, 'children'>> = ({
  items,
  currency = 'USD',
  locale = 'en-US',
  showSubtotals = true,
  editable = false,
  onItemChange,
  neonAccent,
  loading = false,
  ...boxProps
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Group items by category
  const { categoryGroups, summaryItems } = useMemo(() => {
    const groups: Map<string, PriceLineItem[]> = new Map();
    const summary: PriceLineItem[] = [];

    for (const item of items) {
      // Subtotal and total items go to summary
      if (item.type === 'subtotal' || item.type === 'total') {
        summary.push(item);
        continue;
      }

      const category = item.category || 'Other';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(item);
    }

    // Sort items within each category by order
    const categoryGroups: CategoryGroup[] = [];
    for (const [category, categoryItems] of groups) {
      const sortedItems = [...categoryItems].sort((a, b) => a.order - b.order);
      const subtotal = sortedItems.filter((i) => i.type !== 'discount').reduce((sum, i) => sum + i.amount, 0);
      const discounts = sortedItems.filter((i) => i.type === 'discount').reduce((sum, i) => sum + i.amount, 0);

      categoryGroups.push({
        category,
        items: sortedItems,
        subtotal: subtotal - discounts,
      });
    }

    // Sort summary items by order
    const sortedSummary = [...summary].sort((a, b) => a.order - b.order);

    return { categoryGroups, summaryItems: sortedSummary };
  }, [items]);

  const handleItemChange = (itemId: string) => (value: number) => {
    onItemChange?.(itemId, value);
  };

  if (loading) {
    return (
      <Box {...boxProps}>
        <StyledContainer $isDark={isDark} $neonAccent={neonAccent}>
          <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={32} sx={{ mb: 1 }} />
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Skeleton variant="rectangular" height={40} />
          </Box>
        </StyledContainer>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box {...boxProps}>
        <StyledContainer $isDark={isDark} $neonAccent={neonAccent}>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
            No items to display
          </Typography>
        </StyledContainer>
      </Box>
    );
  }

  return (
    <Box {...boxProps}>
      <StyledContainer
        $isDark={isDark}
        $neonAccent={neonAccent}
        initial={motionPresets.fadeInUp.initial}
        animate={motionPresets.fadeInUp.animate}
        transition={motionPresets.fadeInUp.transition}
      >
        {/* Category Groups */}
        {categoryGroups.map(({ category, items: categoryItems, subtotal }) => (
          <Box key={category}>
            <CategoryHeader $isDark={isDark} $neonAccent={neonAccent}>
              {category}
            </CategoryHeader>

            {categoryItems.map((item) => (
              <LineItem
                key={item.id}
                item={item}
                currency={currency}
                locale={locale}
                editable={editable}
                onChange={handleItemChange(item.id)}
                neonAccent={neonAccent}
              />
            ))}

            {/* Category Subtotal */}
            {showSubtotals && categoryGroups.length > 1 && (
              <LineItem
                item={{
                  id: `${category}-subtotal`,
                  description: `${category} Subtotal`,
                  quantity: 1,
                  unitPrice: subtotal,
                  amount: subtotal,
                  type: 'subtotal',
                  order: 999,
                }}
                currency={currency}
                locale={locale}
                neonAccent={neonAccent}
                isSummary
              />
            )}
          </Box>
        ))}

        {/* Summary Section */}
        {summaryItems.length > 0 && (
          <SummarySection $isDark={isDark}>
            {summaryItems.map((item) => (
              <LineItem
                key={item.id}
                item={item}
                currency={currency}
                locale={locale}
                neonAccent={neonAccent}
                isSummary
              />
            ))}
          </SummarySection>
        )}
      </StyledContainer>
    </Box>
  );
};

PriceBreakdown.displayName = 'PriceBreakdown';

export default PriceBreakdown;
